import { useState, useEffect } from 'react';
import { Database, Shield, RefreshCw, Play } from 'lucide-react';
import { getCollectionCoverage, smartSync, SmartSyncResponse, CollectionCoverageData } from '../../services/api';

interface CollectionCoverageProps {
  clientId: string;
  clientName: string;
  refreshTrigger?: number;
}

export default function CollectionCoverage({ clientId, refreshTrigger }: CollectionCoverageProps) {
  const [coverage, setCoverage] = useState<CollectionCoverageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<string | null>(null);

  useEffect(() => {
    loadCoverage();
  }, [clientId]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadCoverage();
    }
  }, [refreshTrigger]);

  const loadCoverage = async () => {
    setLoading(true);
    try {
      const result = await getCollectionCoverage(clientId);
      if (result.success) {
        setCoverage(result);
      }
    } catch {
      // ignore
    }
    setLoading(false);
  };

  const handleSmartSync = async () => {
    setSeeding(true);
    setSeedResult(null);
    try {
      const result: SmartSyncResponse = await smartSync(clientId);
      if (result.success) {
        const parts = [`${result.mode === 'seed' ? 'Seed' : 'Sync'} complete: ${result.forensicUsers} users, ${result.userCount} synced users, ${result.groupCount} groups`];
        if (result.analysis.findingCount > 0) parts.push(`${result.analysis.findingCount} findings`);
        if (result.exoJobId) parts.push('EXO runbook started');
        setSeedResult(parts.join(', '));
        setTimeout(loadCoverage, 3000);
      } else {
        setSeedResult(result.error || 'Smart sync failed');
      }
    } catch (err) {
      setSeedResult(`Error: ${err instanceof Error ? err.message : 'Unknown'}`);
    }
    setSeeding(false);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-4">
        <div className="animate-pulse flex items-center gap-2">
          <div className="h-4 w-4 bg-muted rounded"></div>
          <div className="h-4 w-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-muted flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium text-foreground">Collection Coverage</h3>
        </div>
        <button
          onClick={loadCoverage}
          className="p-1 text-muted-foreground hover:text-foreground rounded"
          title="Refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="p-4">
        {!coverage || coverage.totalCollections === 0 ? (
          <div className="text-center py-4">
            <Shield className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-3">No forensic data collected yet</p>
            <button
              onClick={handleSmartSync}
              disabled={seeding}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 mx-auto"
            >
              {seeding ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Sync Now
                </>
              )}
            </button>
            <p className="text-xs text-muted-foreground mt-2">Automatically seeds 90 days of history on first run</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Data Range</span>
                <p className="font-medium text-foreground">
                  {formatDate(coverage.earliest)} — {formatDate(coverage.latest)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Last Collection</span>
                <p className="font-medium text-foreground">{formatDateTime(coverage.lastCollectionAt)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Total Collections</span>
                <p className="font-medium text-foreground">{coverage.totalCollections}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Sources</span>
                <div className="flex items-center gap-2">
                  <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                    Graph
                  </span>
                  {coverage.hasEXOData ? (
                    <span className="px-1.5 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                      EXO
                    </span>
                  ) : (
                    <span className="px-1.5 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded">
                      No EXO
                    </span>
                  )}
                </div>
              </div>
            </div>

            <p className="pt-2 border-t border-border text-xs text-muted-foreground">
              Use the <strong>Sync Now</strong> button in the page header to refresh all data.
            </p>
          </div>
        )}

        {seedResult && (
          <div className={`mt-3 p-2 rounded text-xs ${
            seedResult.startsWith('Error') || seedResult.startsWith('Seed failed')
              ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
              : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
          }`}>
            {seedResult}
          </div>
        )}
      </div>
    </div>
  );
}
